import React, { memo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { subscriptionsAPI } from '../../api';

const InlineSubscriptionDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  contactName,
  onSuccess 
}) => {
  const [form, setForm] = useState({
    plan_name: '',
    plan_price: 0,
    billing_cycle: 'monthly',
    start_date: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.plan_name.trim()) {
      toast.error('Please enter a plan name');
      return;
    }

    try {
      setLoading(true);
      
      await subscriptionsAPI.create({
        plan_name: form.plan_name,
        plan_price: form.plan_price,
        billing_cycle: form.billing_cycle,
        start_date: form.start_date,
        status: 'active',
        contact_id: contactId
      });
      
      toast.success('Subscription added successfully');
      setForm({
        plan_name: '',
        plan_price: 0,
        billing_cycle: 'monthly',
        start_date: new Date().toISOString().split('T')[0]
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to add subscription');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, onOpenChange, onSuccess]);

  const handleClose = useCallback(() => {
    setForm({
      plan_name: '',
      plan_price: 0,
      billing_cycle: 'monthly',
      start_date: new Date().toISOString().split('T')[0]
    });
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-600" />
            Add Subscription
          </DialogTitle>
          <DialogDescription>
            Create a subscription for {contactName}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subscription-plan">Plan Name *</Label>
            <Input
              id="subscription-plan"
              value={form.plan_name}
              onChange={(e) => setForm(prev => ({ ...prev, plan_name: e.target.value }))}
              placeholder="e.g., Pro Plan"
              data-testid="inline-subscription-plan"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Price ($)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.plan_price}
                onChange={(e) => setForm(prev => ({ ...prev, plan_price: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Billing Cycle</Label>
              <Select value={form.billing_cycle} onValueChange={(v) => setForm(prev => ({ ...prev, billing_cycle: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !form.plan_name.trim()}>
              {loading ? 'Adding...' : 'Add Subscription'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

InlineSubscriptionDialog.displayName = 'InlineSubscriptionDialog';

export default InlineSubscriptionDialog;
